import { useState, useEffect, useCallback } from 'react';
import { Copy, Check, RefreshCw, Mail, Zap, Save, Link2, KeyRound, AlertTriangle, Code2, ChevronDown, ChevronUp, Inbox, PlayCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../services/api';
import { API } from '../App';

function CopyButton({ text, className = '', confirmMessage = null }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${className}`} title="Copy">
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
    </button>
  );
}

export default function CloudflareIncomingSettings({ user }) {
  // FIX: Always point the Worker at PRODUCTION, even when this settings page
  // is being viewed from a preview/staging environment. Emails should never
  // be routed to a temporary preview URL that can restart or disappear.
  const PRODUCTION_ORIGIN = 'https://transport-log.emergent.host';
  const WEBHOOK_URL = `${PRODUCTION_ORIGIN}/api/automations/inbound-email`;
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [localSettings, setLocalSettings] = useState({});
  const [showWorker, setShowWorker] = useState(false);
  const [gmailRelayStatus, setGmailRelayStatus] = useState(null);
  const [checkingGmailRelay, setCheckingGmailRelay] = useState(false);
  const [gmailRelayResult, setGmailRelayResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`${API}/automations/settings`);
      setSettings(res.data);
      setLocalSettings({
        crm_upload_mode: res.data.crm_upload_mode || 'manual',
        email_retention_days: res.data.email_retention_days || 10,
        auto_parse: res.data.auto_parse !== false,
        email_address: res.data.email_address || 'orders@c30dashboard.uk',
      });
    } catch {
      toast.error('Failed to load automation settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get(`${API}/gmail-relay/status`).then(res => setGmailRelayStatus(res.data)).catch(() => {});
  }, []);

  const checkGmailRelayNow = async () => {
    setCheckingGmailRelay(true);
    setGmailRelayResult(null);
    try {
      const res = await api.post(`${API}/gmail-relay/poll-now`);
      setGmailRelayResult(res.data);
      const count = (res.data.processed || []).length;
      if (count > 0) {
        toast.success(`Found ${count} new report${count > 1 ? 's' : ''}! Check the Automations page / BI Reports.`);
      } else {
        toast.info('No new reports found in the mailbox right now.');
      }
    } catch {
      toast.error('Failed to check the Gmail relay mailbox');
    } finally {
      setCheckingGmailRelay(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`${API}/automations/settings`, localSettings);
      toast.success('Settings saved');
      await load();
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const regenerateSecret = async () => {
    if (!window.confirm('Generate a NEW webhook secret? Your Cloudflare Worker will stop working until you update it with the new secret.')) return;
    setRegenerating(true);
    try {
      const res = await api.post(`${API}/automations/settings/regenerate-secret`);
      setSettings(p => ({ ...p, webhook_secret: res.data.webhook_secret }));
      toast.success('New secret generated — update your Cloudflare Worker');
    } catch {
      toast.error('Failed to regenerate secret');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>;
  if (!settings) return null;

  // CRITICAL: the Webhook URL above is hardcoded to always point at production,
  // but the Webhook SECRET is fetched from THIS environment's own backend/database
  // (via `api`/API, which resolves to REACT_APP_BACKEND_URL of wherever this page
  // is currently being viewed). Preview and production are separate databases with
  // DIFFERENT auto-generated secrets — copying/deploying this script while viewing
  // it on preview bakes in preview's secret paired with production's URL, which
  // breaks EVERY inbound email (orders, quotes, M4 reports alike) with a 401
  // "Invalid webhook secret" the moment it's deployed to the real Cloudflare Worker.
  const isViewingProdOrigin = typeof window !== 'undefined' && window.location.origin === PRODUCTION_ORIGIN;

  // Single source of truth for the Worker script — used for BOTH the
  // on-screen preview and the Copy button, with the LIVE webhook URL/secret
  // already baked in. This fixes the earlier bug where the on-screen preview
  // showed placeholder text ("YOUR_WEBHOOK_URL"/"YOUR_SECRET") while the copy
  // button held the real values — copying from the wrong spot (or mixing the
  // two) produced a broken worker.js. Now there is only one version, and it's
  // always ready to paste with no manual editing required.
  const workerScript = `// Cloudflare Email Worker — C30 Dashboard
// Handles: PDF attachments (orders/quotes)
//          CSV / XLSX / XLS attachments (RTOB / BORB / Free Stock reports from M4)
//
// Pure JavaScript — NO npm install needed. Paste this straight into the
// Cloudflare "Quick Edit" editor and Save/Deploy. WEBHOOK_URL and
// WEBHOOK_SECRET below are already your live production values.

const WEBHOOK_URL    = "${WEBHOOK_URL}";
const WEBHOOK_SECRET = "${settings?.webhook_secret || ''}";

// ---- Minimal MIME parser (no dependencies) ----

function splitHeaderBody(block) {
  const m = block.match(/\\r?\\n\\r?\\n/);
  if (!m) return { headers: block, body: '' };
  return { headers: block.slice(0, m.index), body: block.slice(m.index + m[0].length) };
}

function getHeaderValue(headerBlock, name) {
  const unfolded = headerBlock.replace(/\\r?\\n[ \\t]+/g, ' ');
  const re = new RegExp('^' + name + ':[ \\\\t]*(.*)$', 'im');
  const m = unfolded.match(re);
  return m ? m[1].trim() : '';
}

function getHeaderParam(headerValue, paramName) {
  const re = new RegExp(paramName + '\\\\*?\\\\s*=\\\\s*"?([^";]+)"?', 'i');
  const m = headerValue.match(re);
  return m ? m[1].trim() : '';
}

function quotedPrintableToBinary(text) {
  return text
    .replace(/=\\r?\\n/g, '')
    .replace(/=([0-9A-Fa-f]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
}

// Walk the (possibly nested) MIME structure and collect PDF/CSV/XLSX/XLS
// attachments as base64 strings — recurses into multipart/mixed,
// multipart/alternative, multipart/related, etc.
function findAttachments(rawMessage) {
  const attachments = [];
  const ALLOWED_EXT = ['pdf', 'csv', 'xlsx', 'xls'];

  function walk(headerBlock, body) {
    const contentType = getHeaderValue(headerBlock, 'Content-Type') || 'text/plain';
    const disposition = getHeaderValue(headerBlock, 'Content-Disposition') || '';
    const encoding     = (getHeaderValue(headerBlock, 'Content-Transfer-Encoding') || '').toLowerCase();

    if (/^multipart\\//i.test(contentType)) {
      const boundary = getHeaderParam(contentType, 'boundary');
      if (!boundary) return;
      const delimiter = '--' + boundary;
      for (const rawPart of body.split(delimiter)) {
        if (rawPart.startsWith('--') || !rawPart.trim()) continue; // closing marker / preamble
        const part = rawPart.replace(/^\\r?\\n/, '');
        const { headers: partHeaders, body: partBody } = splitHeaderBody(part);
        walk(partHeaders, partBody);
      }
      return;
    }

    let filename = getHeaderParam(disposition, 'filename') || getHeaderParam(contentType, 'name');
    if (!filename) return; // not an attachment part
    filename = filename.replace(/^"|"$/g, '').trim();
    const ext = (filename.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) return;

    const content = body.replace(/\\r?\\n--\\s*$/, '').trim();
    let base64;
    if (encoding === 'base64') {
      base64 = content.replace(/\\s+/g, '');
    } else if (encoding === 'quoted-printable') {
      base64 = btoa(quotedPrintableToBinary(content));
    } else {
      // 7bit / 8bit — 'content' is already a byte-accurate string (see the
      // iso-8859-1 decode below), so btoa() re-encodes it safely.
      base64 = btoa(content);
    }
    attachments.push({ filename, ext, base64 });
  }

  const { headers, body } = splitHeaderBody(rawMessage);
  walk(headers, body);
  return attachments;
}

export default {
  async email(message, env, ctx) {
    const rawBuffer = await new Response(message.raw).arrayBuffer();
    // iso-8859-1 maps every byte 1:1 to a char code (0-255) — this lets us
    // safely split on MIME boundaries and btoa() binary attachments below
    // without corrupting them (unlike TextDecoder('utf-8')).
    const rawMessage = new TextDecoder('iso-8859-1').decode(rawBuffer);
    const subjectMatch = rawMessage.match(/^Subject:[ \\t]*(.*)$/im);
    const subject = subjectMatch ? subjectMatch[1].trim() : '';

    const attachments = findAttachments(rawMessage);

    // M4 sometimes attaches a standard PDF (e.g. terms & conditions) ALONGSIDE
    // the real CSV/XLSX report in the SAME email. Track each attachment's own
    // filename separately (a single shared 'filename' variable here previously
    // got silently overwritten by whichever attachment was found last, causing
    // the CSV/XLSX report's real filename — and therefore its detection — to
    // be lost whenever a PDF was also present).
    let pdfBase64 = '', fileBase64 = '', pdfFilename = '', fileFilename = '';
    for (const att of attachments) {
      if (att.ext === 'pdf') { pdfBase64 = att.base64; pdfFilename = att.filename; }
      else { fileBase64 = att.base64; fileFilename = att.filename; }
    }

    // Nothing to process
    if (!pdfBase64 && !fileBase64) {
      console.log('[C30 Worker] No PDF or CSV/XLSX attachment — skipping', message.from);
      return;
    }

    const payload = {
      secret:     WEBHOOK_SECRET,
      from_email: message.from,
      subject,
      ...(pdfBase64  ? { pdf_base64:  pdfBase64,  filename: pdfFilename } : {}),
      ...(fileBase64 ? { file_base64: fileBase64, file_filename: fileFilename, file_type: (fileFilename.split('.').pop() || '').toLowerCase() } : {}),
    };

    try {
      const res = await fetch(WEBHOOK_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
      const text = await res.text();
      console.log('[C30 Worker] Webhook response:', res.status, text.slice(0, 200));
    } catch (err) {
      console.error('[C30 Worker] Webhook error:', err);
    }
  },
};`;

  return (
    <div className="space-y-6 max-w-4xl">

      {!isViewingProdOrigin && (
        <div
          data-testid="cloudflare-wrong-environment-warning"
          className="flex items-start gap-3 p-4 bg-red-50 border-2 border-red-300 rounded-xl"
        >
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-red-800">
              You're viewing this on a preview/staging environment — the Webhook Secret shown below belongs to THIS environment's database, not your live production site.
            </p>
            <p className="text-xs text-red-700 mt-1">
              Copying or deploying the script below right now would pair your PRODUCTION webhook URL with the WRONG secret, breaking every inbound email (orders, quotes, and M4 reports alike) with "Invalid webhook secret". Go to{' '}
              <strong>{PRODUCTION_ORIGIN}</strong> and open this exact same page there — only copy/deploy the script from that page.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-100">
        <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center flex-shrink-0">
          <Mail className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">Cloudflare Email Automation</h3>
          <p className="text-sm text-gray-600 mt-0.5">
            Receive emails with PDF, CSV or XLSX attachments at your custom address — orders/quotes, the RTOB Daily Order Entry Report, the BORB Order Book Summary, and the Free Stock Report. Cloudflare routes them to this dashboard automatically — <strong>100% free</strong> up to 100,000 emails/day.
          </p>
        </div>
      </div>

      {/* Incoming Email Address */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h4 className="font-semibold text-gray-800 flex items-center gap-2">
          <Zap className="w-4 h-4 text-orange-500" /> Incoming Email
        </h4>
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Incoming Email Address</label>
          <div className="flex items-center gap-2">
            <input
              value={localSettings.email_address || ''}
              onChange={e => setLocalSettings(p => ({ ...p, email_address: e.target.value }))}
              placeholder="orders@c30dashboard.uk"
              className="flex-1 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 text-sm font-mono text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-300"
            />
            <CopyButton text={localSettings.email_address || ''} />
          </div>
          <p className="text-xs text-gray-400 mt-1">Configure this address as a route in Cloudflare Email Routing.</p>
        </div>
      </div>

      {/* Webhook Endpoint & Secret — for the Cloudflare Worker */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4" data-testid="webhook-config-card">
        <h4 className="font-semibold text-gray-800 flex items-center gap-2">
          <Link2 className="w-4 h-4 text-blue-500" /> Cloudflare Worker Configuration
        </h4>
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">
            The Webhook URL always points at your <strong>live production site</strong> ({PRODUCTION_ORIGIN}), no matter which environment you're viewing this page from. The <strong>Secret</strong> below is NOT hardcoded the same way — it belongs to whichever environment's database you're currently viewing. Only copy/deploy the Secret (or the full script below) while viewing this page on {PRODUCTION_ORIGIN} itself, otherwise it won't match production and every inbound email will fail with "Invalid webhook secret".
          </p>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Webhook URL</label>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={WEBHOOK_URL}
              data-testid="webhook-url-input"
              className="flex-1 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 text-sm font-mono text-gray-700 focus:outline-none"
            />
            <CopyButton text={WEBHOOK_URL} />
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Webhook Secret</label>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={settings.webhook_secret || ''}
              data-testid="webhook-secret-input"
              className="flex-1 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 text-sm font-mono text-gray-700 focus:outline-none"
            />
            <CopyButton
              text={settings.webhook_secret || ''}
              confirmMessage={
                isViewingProdOrigin
                  ? null
                  : `WARNING: You're on a preview/staging environment. This secret belongs to THIS environment, not production. Using it in your production Worker will break ALL inbound emails with "Invalid webhook secret".\n\nGo to ${PRODUCTION_ORIGIN} instead and copy it from there.\n\nCopy anyway?`
              }
            />
            <Button
              onClick={regenerateSecret}
              disabled={regenerating}
              variant="outline"
              size="sm"
              data-testid="regenerate-secret-btn"
              className="rounded-lg whitespace-nowrap"
            >
              {regenerating ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
              <span className="ml-1.5">Regenerate</span>
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-1">In <code className="font-mono">worker.js</code>: set <code className="font-mono">WEBHOOK_URL</code> and <code className="font-mono">WEBHOOK_SECRET</code> to the values above.</p>
        </div>
      </div>

      {/* Worker Script — copy-pasteable updated Worker that handles PDF + CSV/XLSX */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
        <button
          className="w-full flex items-center justify-between"
          onClick={() => setShowWorker(v => !v)}
        >
          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
            <Code2 className="w-4 h-4 text-green-600" /> Updated Worker Script
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">PDF + RTOB + BORB + Free Stock</span>
          </h4>
          {showWorker ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {showWorker && (
          <>
            <p className="text-sm text-gray-500">
              Copy this script into your Cloudflare Worker (<code className="font-mono text-xs">worker.js</code>) and deploy — the webhook URL and secret are already filled in, <strong>nothing to replace</strong>. It forwards <strong>any</strong> PDF or CSV/XLSX attachment as-is — the backend auto-detects which report it is, so <strong>one</strong> Worker + <strong>one</strong> inbox (<code className="font-mono text-xs">orders@c30dashboard.uk</code>) now covers all of these, no extra setup needed:
            </p>
            <ul className="text-sm text-gray-600 list-disc pl-5 space-y-0.5">
              <li>PDF orders / quotes</li>
              <li>M4 "Total Sales Order Book" — Daily Order Entry Report (RTOB)</li>
              <li>M4 Order Book Summary (BORB) — powers the BI Reports Order Book</li>
              <li>Free Stock Report (CSV or XLSX)</li>
            </ul>
            <div className="relative">
              <pre className="bg-gray-900 text-green-300 text-xs rounded-xl p-4 overflow-x-auto leading-relaxed whitespace-pre">
                {workerScript}
              </pre>
              <CopyButton
                text={workerScript}
                className="absolute top-3 right-3 bg-gray-800 border border-gray-600 rounded-lg"
                confirmMessage={
                  isViewingProdOrigin
                    ? null
                    : `WARNING: You're on a preview/staging environment. This script's secret belongs to THIS environment, not production. Deploying it will break ALL inbound emails on production (orders, quotes, M4 reports) with "Invalid webhook secret".\n\nGo to ${PRODUCTION_ORIGIN} instead and copy it from there.\n\nCopy anyway?`
                }
              />
            </div>
            <p className="text-xs text-gray-400">
              <strong>Setup:</strong> Paste this directly into the Cloudflare Worker's <code className="font-mono">Quick Edit</code> editor — no <code className="font-mono">npm install</code> or bundling required. Click <code className="font-mono">Save and Deploy</code>. Nothing to edit — the URL and secret above are already baked in.
            </p>
          </>
        )}
      </div>

      {/* Gmail Relay — fallback M4 report ingestion mailbox */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4" data-testid="gmail-relay-card">
        <h4 className="font-semibold text-gray-800 flex items-center gap-2">
          <Inbox className="w-4 h-4 text-red-500" /> Gmail Relay (M4 Fallback)
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Backup path</span>
        </h4>
        <p className="text-sm text-gray-500">
          A fallback destination for M4's automated reports if `orders@c30dashboard.uk` can't be used directly. Point M4's report recipient at{' '}
          <strong className="font-mono text-gray-700">{gmailRelayStatus?.relay_email || 'the relay mailbox'}</strong>{' '}
          — a background check runs automatically every 3 minutes, or click the button below to check right now.
        </p>
        <div className="flex items-center gap-3">
          <Button
            onClick={checkGmailRelayNow}
            disabled={checkingGmailRelay || !gmailRelayStatus?.configured}
            data-testid="gmail-relay-check-now-btn"
            variant="outline"
            className="rounded-lg"
          >
            {checkingGmailRelay ? <RefreshCw className="w-4 h-4 animate-spin mr-1.5" /> : <PlayCircle className="w-4 h-4 mr-1.5" />}
            Check Now
          </Button>
          {gmailRelayStatus && !gmailRelayStatus.configured && (
            <span className="text-xs text-red-500">Not configured — contact support.</span>
          )}
        </div>
        {gmailRelayResult && (
          <div data-testid="gmail-relay-result" className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
            {(gmailRelayResult.processed || []).length === 0 ? (
              <p className="text-gray-500">No new reports found in the mailbox.</p>
            ) : (
              <ul className="space-y-1">
                {gmailRelayResult.processed.map((p, i) => (
                  <li key={i} className="text-gray-700">
                    <span className="font-medium">{p.filename}</span> — {p.message || p.status}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* CRM Upload Controls — Super Admin */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h4 className="font-semibold text-gray-800 flex items-center gap-2">
          <Zap className="w-4 h-4 text-purple-500" /> CRM Upload Controls
          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">Super Admin</span>
        </h4>
        <p className="text-sm text-gray-500">Control how parsed emails and documents are uploaded to Zoho CRM. Additional automation logic will be built progressively.</p>

        <div className="space-y-2">
          {[
            { value: 'manual', label: 'Manual', desc: 'Nothing is uploaded automatically. Users review and upload each document manually.' },
            { value: 'semi_auto', label: 'Semi-Auto', desc: 'System highlights pending uploads. Users approve before each CRM upload.' },
            { value: 'auto', label: 'Fully Automatic', desc: 'All parsed documents are automatically uploaded to CRM. Use with caution.' },
          ].map(opt => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                localSettings.crm_upload_mode === opt.value
                  ? 'border-purple-300 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="crm_upload_mode"
                value={opt.value}
                checked={localSettings.crm_upload_mode === opt.value}
                onChange={() => setLocalSettings(p => ({ ...p, crm_upload_mode: opt.value }))}
                className="mt-0.5 accent-purple-600"
              />
              <div>
                <span className="font-medium text-gray-800 text-sm">{opt.label}</span>
                <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
              </div>
            </label>
          ))}
        </div>

        <div className="flex items-center gap-4 pt-1">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={localSettings.auto_parse !== false}
              onChange={e => setLocalSettings(p => ({ ...p, auto_parse: e.target.checked }))}
              className="accent-purple-600"
            />
            <span className="text-sm text-gray-700">Auto-parse PDF on arrival with GPT-4 mini</span>
          </label>
          <div className="flex items-center gap-2 ml-auto">
            <label className="text-sm text-gray-600">Retention</label>
            <select
              value={localSettings.email_retention_days || 10}
              onChange={e => setLocalSettings(p => ({ ...p, email_retention_days: Number(e.target.value) }))}
              className="border border-gray-200 rounded-lg px-2 py-1 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-300"
            >
              {[3, 5, 7, 10, 14, 30].map(d => <option key={d} value={d}>{d} days</option>)}
            </select>
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="text-white rounded-lg" style={{ backgroundColor: '#7c3aed' }}>
          {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save Settings
        </Button>
      </div>

    </div>
  );
}
